// Imports
import { faEarthAmericas, faLocationDot } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Params, useParams } from 'react-router-dom';
import styled from 'styled-components/macro';
import { useAppDispatch } from '../../../../../../../shareables/foundation/front-end/redux/hooks';
import apiActions from '../../../../../../../shareables/integrations/api/redux/actions';
import theme from '../../../../../../../shareables/theme';
import { GetAccountResponse } from '../../../../../../../shareables/types/api/main/website/page/type/207/account/get';
import Addresses from './addresses';
import Member from './member';


// Styled components
const Container = styled.div`
	h3 {
		font-size: 1.5rem;
		font-weight: 500;
		line-height: 1.42857143;
	}
	
	h4 {
		font-size: 1.125rem;
		font-weight: 500;
		line-height: 1.1;
	}
	
	hr {
		border: 0;
		border-top: 0.0625rem solid #eee;
		margin: 0.9375rem 0;
	}
`;

const AddressGroup = styled.div`
	display: flex;
	flex-wrap: wrap;
	
	hr {
		margin: 0.5rem 0 0;
	}
	
	@media (max-width: ${theme.breakpoints.mobile}px) {
		flex-direction: column;
		gap: 1rem;
	}
`;

const AddressContainer = styled.div`
	flex: 1;
`;

const AddressHeader = styled.h4`
	display: flex;
	align-items: center;
	gap: 0.5rem;
	margin-bottom: 0.5rem;
`;


// Define the accepted props
interface Props {
	pageID: number;
	pageTitle: string;
	storybookAccount?: GetAccountResponse;
}


// Function component
const AccountDetails: React.FC<Props> = ({ pageID, pageTitle, storybookAccount }) => {
	// Use state
	const [account, setAccount] = useState<GetAccountResponse | null>(storybookAccount ?? null);
	
	
	// Use Redux functionality
	const dispatch = useAppDispatch();
	
	
	// Use React Router functionality
	const { accountIdentifier } = useParams<Params>();
	
	
	// Load account on mount
	useEffect(() => {
		void dispatch(
			apiActions.call<undefined, undefined, GetAccountResponse>({
				action: 'GET',
				uri: `/website/${window.location.hostname}/page/id_${pageID}/type/207/account/${accountIdentifier ?? ''}`,
				completion: (json) => {
					setAccount(json.response);
				},
			})
		);
	}, [dispatch, pageID, accountIdentifier]);
	
	
	// If loading, then display nothing
	if (account === null) {
		return null;
	}
	
	
	// Return JSX
	return (
		<Container>
			<Helmet title={`${account.consolidatedName} | ${pageTitle}`} />
			
			<h3>{account.consolidatedName}</h3>
			
			{(account.internalAddresses.length > 0 || account.externalAddresses.length > 0) && (
				<React.Fragment>
					<hr />
					<AddressGroup>
						{account.internalAddresses.length > 0 && (
							<AddressContainer>
								<AddressHeader>
									<FontAwesomeIcon icon={faLocationDot} />
									Community addresses
								</AddressHeader>
								
								<Addresses addresses={account.internalAddresses} />
							</AddressContainer>
						)}
						
						{account.externalAddresses.length > 0 && (
							<AddressContainer>
								<AddressHeader>
									<FontAwesomeIcon icon={faEarthAmericas} />
									External addresses
								</AddressHeader>
								
								<Addresses addresses={account.externalAddresses} />
							</AddressContainer>
						)}
					</AddressGroup>
				</React.Fragment>
			)}
			
			{account.members.map((member, i) => (
				<React.Fragment key={i}>
					<hr />
					<Member member={member} />
				</React.Fragment>
			))}
		</Container>
	);
};

export default AccountDetails;
